import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, useTheme, IconButton, Tooltip } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FilterDropdown from "./FilterDropdown";
import TeamMemberRow from "./TeamMemberRow";
import DashboardRowSkeleton from "./DashboardRowSkeleton";
import { fetchTeamMembersPanel, fetchDashboardTeamOptions } from "../../../../redux/Slice/Dashboard/teamMembersPanelSlice";
import { setSelectedOption } from "../../../../redux/Slice/Settings/settingsSlice";

const ALL_TEAMS = "All Teams";
const ALL_STATUSES = "All Statuses";
// Fixed, known-ahead-of-time set (unlike Teams, which is account-specific and
// fetched below) — matches getTeamMembersPanel.service.js's STATUS_MATCH keys
// once lowercased, same convention as ContactsPanel.jsx's local TABS.
const MEMBER_STATUSES = [ALL_STATUSES, "Assigned", "Unassigned", "Online", "Away"];
const PAGE_SIZE = 5;

// Same small dark-bubble tooltip convention as ContactsPanel.jsx's TAB_TOOLTIP_SX.
const HEADING_TOOLTIP_SX = {
  tooltip: {
    sx: {
      backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#E2E2E4" : "#1A1A1A"),
      color: (theme) => (theme.palette.mode === "dark" ? "#1A1A1A" : "#F5F5F5"),
      fontSize: "0.75rem",
      fontWeight: 500,
      borderRadius: "8px",
      maxWidth: 190,
      textAlign: "center",
      px: 1.25,
      py: 0.75,
      boxShadow: (theme) =>
        theme.palette.mode === "dark" ? "0 6px 16px rgba(0,0,0,0.35)" : "0 4px 12px rgba(0,0,0,0.15)",
    },
  },
  arrow: {
    sx: {
      color: (theme) => (theme.palette.mode === "dark" ? "#E2E2E4" : "#1A1A1A"),
    },
  },
};

function TeamMembersPanel({ statusIcons }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountid } = useParams();

  const members = useSelector((state) => state.teamMembersPanel.members);
  const totalCount = useSelector((state) => state.teamMembersPanel.totalCount);
  const loading = useSelector((state) => state.teamMembersPanel.loading);
  const teamOptions = useSelector((state) => state.teamMembersPanel.teamOptions);

  const [team, setTeam] = useState(ALL_TEAMS);
  const [status, setStatus] = useState(ALL_STATUSES);
  const [page, setPage] = useState(1);
  const [pageDirection, setPageDirection] = useState(1); // 1 = next, -1 = prev — same convention as ContactsPanel.jsx

  const teams = useMemo(() => {
    const names = [...new Set((teamOptions || []).map((t) => t.teamName).filter(Boolean))].sort();
    return [ALL_TEAMS, ...names];
  }, [teamOptions]);

  useEffect(() => {
    dispatch(fetchDashboardTeamOptions());
  }, [dispatch]);

  // Filter changes restart pagination — a stale page index from a longer,
  // unfiltered roster shouldn't strand the user past the end of a shorter one.
  useEffect(() => {
    setPage(1);
  }, [team, status]);

  useEffect(() => {
    dispatch(
      fetchTeamMembersPanel({
        teamName: team === ALL_TEAMS ? undefined : team,
        status: status === ALL_STATUSES ? undefined : status.toLowerCase(),
        page,
        limit: PAGE_SIZE,
      })
    );
  }, [dispatch, team, status, page]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const showInitialLoading = loading && members.length === 0;

  const NOTCH_W = 476;
  const NOTCH_H = 60;
  const R = 16;
  const divider = theme.palette.divider;
  // 90-degree quarter-circle arcs, sampled at 22.5-degree steps: point(t) = center + R*(sin t, -cos t).
  const arc1 = [ // convex, center (100%-W-R, R): top edge -> vertical cut edge
    [NOTCH_W + R, 0],
    [NOTCH_W + 9.88, 1.22],
    [NOTCH_W + 4.69, 4.69],
    [NOTCH_W + 1.22, 9.88],
    [NOTCH_W, R],
  ].map(([n, y]) => `calc(100% - ${n}px) ${y}px`).join(", ");
  const arcConcave = [ // concave, center (100%-W, H): the notch's inner point
    [0, NOTCH_H - R],
    [6.12, NOTCH_H - 14.78],
    [11.31, NOTCH_H - 11.31],
    [14.78, NOTCH_H - 6.12],
    [R, NOTCH_H],
  ].map(([dx, y]) => `calc(100% - ${NOTCH_W - dx}px) ${y}px`).join(", ");
  const arc2 = [ // convex, center (100%-R, H+R): horizontal cut edge -> right edge
    [R, NOTCH_H],
    [9.88, NOTCH_H + 1.22],
    [4.69, NOTCH_H + 4.69],
    [1.22, NOTCH_H + 9.88],
    [0, NOTCH_H + R],
  ].map(([n, y]) => `calc(100% - ${n}px) ${y}px`).join(", ");
  return (
    <Box sx={{ flex: 1, position: "relative", display: "flex", flexDirection: "column" }}>
      <Box sx={{
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${divider}`,
        borderRadius: "24px",
        p: 2,
        minHeight: 520,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        clipPath: `polygon(0 0, ${arc1}, ${arcConcave}, ${arc2}, 100% 100%, 0 100%)`,
      }}>
      <Box sx={{ mb: 3 }}>
        <Tooltip
          title="Everyone on your team, with how many contacts each is currently handling."
          placement="bottom"
          arrow
          componentsProps={HEADING_TOOLTIP_SX}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "1rem", display: "inline-block", cursor: "default" }}>
            Team Members
          </Typography>
        </Tooltip>
      </Box>
      <Box sx={{ display: "flex", gap: 1.25, mb: 2.25, fontSize: "0.875rem" }}>
        <FilterDropdown options={teams} value={team} onChange={setTeam} width={170} searchable />
        <FilterDropdown options={MEMBER_STATUSES} value={status} onChange={setStatus} icons={statusIcons} width={145} />
      </Box>
      {showInitialLoading ? (
        <DashboardRowSkeleton count={PAGE_SIZE} />
      ) : members.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            textAlign: "center",
            padding: 0,
          }}
        >
          <Box sx={{ mb: 2, color: (theme) => theme.palette.text.secondary, display: "flex" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="58" height="58" viewBox="0 0 24 24" fill="none">
              <g clipPath="url(#clip0_3111_32737)">
                <path d="M18 18.86H17.24C16.44 18.86 15.68 19.17 15.12 19.73L13.41 21.42C12.63 22.19 11.36 22.19 10.58 21.42L8.87 19.73C8.31 19.17 7.54 18.86 6.75 18.86H6C4.34 18.86 3 17.53 3 15.89V4.97C3 3.33 4.34 2 6 2H18C19.66 2 21 3.33 21 4.97V15.88C21 17.52 19.66 18.86 18 18.86Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12.0701 8.9498C12.0301 8.9498 11.97 8.9498 11.92 8.9498C10.87 8.9098 10.04 8.0598 10.04 6.9998C10.04 5.9198 10.9101 5.0498 11.9901 5.0498C13.0701 5.0498 13.9401 5.9298 13.9401 6.9998C13.9501 8.0598 13.1201 8.9198 12.0701 8.9498Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9.24994 11.96C7.91994 12.85 7.91994 14.3 9.24994 15.19C10.7599 16.2 13.2399 16.2 14.7499 15.19C16.0799 14.3 16.0799 12.85 14.7499 11.96C13.2399 10.96 10.7699 10.96 9.24994 11.96Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_3111_32737">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </Box>
          <Typography variant="h6" color="text.secondary">
            No team members found
          </Typography>
          <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: 0.5 }}>
            Try a different team or status filter.
          </Typography>
        </Box>
      ) : (
        // No overflow:hidden here — matches ContactsPanel.jsx's identical wrapper
        // (avoids clipping issues if row-level negative-margin styling is ever added here too).
        <Box sx={{ position: "relative" }}>
          <AnimatePresence mode="wait" initial={false} custom={pageDirection}>
            <motion.div
              key={page}
              custom={pageDirection}
              initial={(dir) => ({ opacity: 0, x: dir * 16 })}
              animate={{ opacity: 1, x: 0 }}
              exit={(dir) => ({ opacity: 0, x: dir * -16 })}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {members.map((m, idx) => (
                <TeamMemberRow key={m.id} member={m} isLast={idx === members.length - 1} />
              ))}
            </motion.div>
          </AnimatePresence>
        </Box>
      )}
      <Box sx={{ mt: "auto", pt: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            size="small"
            disabled={page === 1}
            onClick={() => { setPageDirection(-1); setPage((p) => Math.max(1, p - 1)); }}
            sx={{ width: 26, height: 26, border: `1px solid ${theme.palette.divider}` }}
          >
            <ChevronLeftIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "text.secondary" }}>
            Page {page} of {totalPages}
          </Typography>
          <IconButton
            size="small"
            disabled={page >= totalPages}
            onClick={() => { setPageDirection(1); setPage((p) => Math.min(totalPages, p + 1)); }}
            sx={{ width: 26, height: 26, border: `1px solid ${theme.palette.divider}` }}
          >
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        <Typography
          onClick={() => {
            dispatch(setSelectedOption("Team Members"));
            navigate(`/home/${accountid}/settings`);
          }}
          sx={{ fontSize: "0.75rem", fontWeight: 600, color: "text.secondary", border: `1px solid ${theme.palette.divider}`, borderRadius: "8px", px: 1.25, py: 0.5, cursor: "pointer" }}
        >
          View All Members
        </Typography>
      </Box>
      </Box>
      <Box sx={{ position: "absolute", top: R, right: NOTCH_W, width: "1px", height: NOTCH_H - 2 * R, borderRight: `1px solid ${divider}`, pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", top: NOTCH_H, right: R, width: NOTCH_W - 2 * R, height: "1px", borderTop: `1px solid ${divider}`, pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", top: NOTCH_H - R, right: NOTCH_W - R, width: R, height: R, borderBottomLeftRadius: `${R}px`, borderLeft: `1px solid ${divider}`, borderBottom: `1px solid ${divider}`, pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", top: 0, right: NOTCH_W, width: R, height: R, borderTopRightRadius: `${R}px`, borderTop: `1px solid ${divider}`, borderRight: `1px solid ${divider}`, pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", top: NOTCH_H, right: 0, width: R, height: R, borderTopRightRadius: `${R}px`, borderTop: `1px solid ${divider}`, borderRight: `1px solid ${divider}`, pointerEvents: "none" }} />
    </Box>
  );
}

export default TeamMembersPanel;
